import { useApiClient } from '@m43/core';
import type { ConnectionStatusHistoryEntry } from '@m43/bridge-interface';
import {
    ConnectionStatusEnumType,
    ConnectionStatusReportView,
    ConnectionStatusView,
    MetricsResource,
} from '@mark43/cad-api';
import React from 'react';
import { v4 } from 'uuid';
import { useApplicationType } from '../common/hooks/use-application-type';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { CadNextSocketListener, useSocketStatus } from '../socket/use-stream';
import {
    ConnectionStatusContext,
    ConnectionStatusContextValue,
    ConnectionStatusContextData,
} from './connection-status-context';
import { convertCombinedConnectionStatusReportToFrontendConnectionStatusDto } from './converters';

const CONNECTION_STATUS_HISTORY_CAPACITY = 50;

let socketLastOfflineEpoch: number | undefined = undefined;

export function ConnectionStatusProvider(props: { children: React.ReactNode }) {
    const { isFirstResponder, isCadLegacyElectron, isElectronRewrite } = useApplicationType();

    const historyRef = React.useRef<ConnectionStatusHistoryEntry[]>([]);

    const recordStatusChange = React.useCallback((source: string, status: string) => {
        const history = historyRef.current;
        if (history.length >= CONNECTION_STATUS_HISTORY_CAPACITY) {
            history.shift();
        }
        history.push({ source, status, timestamp: Date.now() });
    }, []);

    const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatusContextValue>({
        SOCKET: 'ONLINE',
        INTERNET: electronBridge?.args['--connection-status'] ?? 'ONLINE',
        MARK43_PING: electronBridge?.args['--mark43-ping-status'] ?? 'ONLINE',
        DNS_RESOLUTION: 'ONLINE',
        FARO: 'ONLINE',
    });

    const apiClient = useApiClient();

    const socketStatusListener: CadNextSocketListener = React.useMemo(
        () => ({
            invokeOnInitialize: false,
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (socketStatusChangeEvent.timesConnected < 1) return;
                const newStatus: ConnectionStatusEnumType =
                    socketStatusChangeEvent.status === 'CONNECTED' ? 'ONLINE' : 'OFFLINE';
                if (newStatus === 'OFFLINE' && !socketLastOfflineEpoch) {
                    socketLastOfflineEpoch = Date.now();
                }
                setConnectionStatus((current) => {
                    if (newStatus === current.SOCKET) return current;
                    recordStatusChange('SOCKET', newStatus);
                    return {
                        ...current,
                        SOCKET: newStatus,
                    };
                });
                apiClient.request<MetricsResource.PostConnectionStatusViews>({
                    method: 'POST',
                    url: 'metrics/report_connection_status/',
                    data: {
                        connectionStatusViews: [
                            {
                                source: 'SOCKET',
                                status: newStatus,
                                podName: socketStatusChangeEvent.podName,
                            },
                        ],
                    },
                });
                if (socketStatusChangeEvent.status === 'CONNECTED' && socketLastOfflineEpoch) {
                    const epoch = socketLastOfflineEpoch;
                    socketLastOfflineEpoch = undefined;
                    const delta = Date.now() - epoch;
                    const seconds = Math.round(delta / 1000);
                    apiClient.request<MetricsResource.PostOfflineSecondsViews>({
                        method: 'POST',
                        url: 'metrics/report_offline_seconds/',
                        data: {
                            offlineSecondsViews: [
                                {
                                    source: 'SOCKET',
                                    offlineSeconds: seconds,
                                },
                            ],
                        },
                    });
                }
            },
        }),
        [apiClient, recordStatusChange]
    );

    useSocketStatus(socketStatusListener);

    React.useEffect(() => {
        if (isCadLegacyElectron) {
            const onInternetConnectionStatusUpdate = (
                connectionStatus: ConnectionStatusEnumType
            ) => {
                setConnectionStatus((current) => {
                    if (connectionStatus === current.INTERNET) return current;
                    recordStatusChange('INTERNET', connectionStatus);
                    return {
                        ...current,
                        INTERNET: connectionStatus,
                    };
                });
            };
            if (electronBridge && 'registerIsOnlineCallback' in electronBridge) {
                electronBridge.registerIsOnlineCallback(onInternetConnectionStatusUpdate);
            }
            setConnectionStatus((current) => ({
                ...current,
                INTERNET: electronBridge?.args['--connection-status'] ?? 'ONLINE',
            }));
        }
    }, [setConnectionStatus, isCadLegacyElectron, recordStatusChange]);

    React.useEffect(() => {
        if (electronBridge && 'registerConnectionStatusViewHandler' in electronBridge) {
            const onConnectionStatusViewUpdate = (view: ConnectionStatusView) => {
                setConnectionStatus((current) => {
                    if (current[view.source] !== view.status) {
                        recordStatusChange(view.source, view.status);
                        return {
                            ...current,
                            [view.source]: view.status,
                        };
                    }
                    return current;
                });
            };
            electronBridge.registerConnectionStatusViewHandler?.(onConnectionStatusViewUpdate);
        }
    }, [isElectronRewrite, recordStatusChange]);

    React.useEffect(() => {
        // this entire if block can be removed when new electron is installed at all customers
        if (
            isCadLegacyElectron &&
            electronBridge?.args['--workspace-window-type'] === 'EVENT_V2' &&
            !window.Cypress
        ) {
            const combinedConnectionStatusReport: ConnectionStatusReportView = {
                uuid: v4(),
                timestamp: Date.now().toString(),
                internetStatus: {
                    status: connectionStatus.INTERNET,
                    // hardcoded because offlineSeconds is unused from the endpoint we call here
                    offlineSeconds: 0,
                },
                socketStatus: {
                    status: connectionStatus.SOCKET,
                    // hardcoded because offlineSeconds is unused from the endpoint we call here
                    offlineSeconds: 0,
                },
            };
            apiClient
                .request<MetricsResource.PostCombinedConnectionStatusReports>({
                    method: 'POST',
                    url: 'metrics/combined_connection_status_reports/',
                    data: { connectionStatusReportViews: [combinedConnectionStatusReport] },
                })
                .catch(() => {
                    if (electronBridge && 'storeConnectionStatusDto' in electronBridge) {
                        electronBridge.storeConnectionStatusDto?.(
                            convertCombinedConnectionStatusReportToFrontendConnectionStatusDto(
                                combinedConnectionStatusReport
                            )
                        );
                    }
                });
        }
    }, [apiClient, connectionStatus, isFirstResponder, isCadLegacyElectron]);

    const contextData = React.useMemo<ConnectionStatusContextData>(
        () => ({
            status: connectionStatus,
            history: historyRef.current,
        }),
        [connectionStatus]
    );

    return (
        <ConnectionStatusContext.Provider value={contextData}>
            {props.children}
        </ConnectionStatusContext.Provider>
    );
}
